import * as React from 'react';

import { Avatar, Box, Button, ButtonBase, Chip, Typography } from '@mui/material';

import styles from './FuzzyFindDialog.module.css';
import { Suggestion } from './nameRepair';

export interface RepairRow {
  pasted: string;
  suggestions: Suggestion[];
  /** Catalog name the pasted name is repaired to, if any. */
  chosen: string | null;
}

interface Props {
  rows: RepairRow[];
  images: Map<number, string>;
  onChoose: (pasted: string, name: string | null) => void;
  onAcceptAll: () => void;
}

const captionFor = (name: string, suggestions: Suggestion[]) => {
  if (suggestions[0]?.entry.name.toLowerCase() === name.trim().toLowerCase()) return 'Exact match';
  return suggestions.length ? 'Closest matches' : 'No close matches';
};

const metadata = ({ entry }: Suggestion) => {
  const workspaces = Array.from(new Set(entry.products.map((p) => p.workspace)));
  const reviews = entry.products.reduce((n, p) => n + p.reviews, 0);
  const category = entry.products.find((p) => p.category)?.category ?? 'Uncategorised';
  return `${workspaces.join(', ')} · ${category} · ${reviews} reviews`;
};

/** Per-name choice among the closest catalog products, or Skip. */
const NameRepairList: React.FC<Props> = ({ rows, images, onChoose, onAcceptAll }) => (
  <Box className={styles.repair}>
    <Box className={styles.repairHeader}>
      <Typography variant="subtitle2">Closest products</Typography>
      {rows.some((r) => r.suggestions.length > 0) && (
        <Button size="small" onClick={onAcceptAll}>
          Accept all best matches
        </Button>
      )}
    </Box>
    {rows.map(({ pasted, suggestions, chosen }) => (
      <Box key={pasted} className={styles.repairRow}>
        <Box className={styles.pasted}>
          <Typography variant="body2" fontWeight={600}>
            {pasted}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {captionFor(pasted, suggestions)}
          </Typography>
        </Box>
        <Box role="radiogroup" aria-label={`Suggestions for ${pasted}`} className={styles.suggestions}>
          {suggestions.map((s) => {
            const selected = chosen === s.entry.name;
            const thumb = images.get(s.entry.products[0].id);
            return (
              <ButtonBase
                key={s.entry.name}
                role="radio"
                aria-checked={selected}
                className={selected ? `${styles.card} ${styles.cardSelected}` : styles.card}
                onClick={() => onChoose(pasted, selected ? null : s.entry.name)}
              >
                <Avatar variant="rounded" src={thumb} alt="" className={styles.thumb}>
                  {s.entry.name.charAt(0)}
                </Avatar>
                <Box className={styles.cardText}>
                  <Typography variant="body2" noWrap>
                    {s.entry.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap component="div">
                    {metadata(s)}
                  </Typography>
                </Box>
                <Chip size="small" label={`${Math.round(s.score * 100)}%`} variant={selected ? 'filled' : 'outlined'} />
              </ButtonBase>
            );
          })}
          {suggestions.length > 0 && (
            <ButtonBase
              role="radio"
              aria-checked={chosen === null}
              aria-label={`Skip ${pasted}`}
              className={`${styles.card} ${styles.skip} ${chosen === null ? styles.cardSelected : ''}`}
              onClick={() => onChoose(pasted, null)}
            >
              <Typography variant="body2">Skip</Typography>
            </ButtonBase>
          )}
        </Box>
      </Box>
    ))}
  </Box>
);

export default NameRepairList;
